import { Trans, useTranslation } from 'react-i18next';

import { Body2, Stack } from '@myphx-engineering/phx-design-system';
import { constants } from '@myphx-engineering/phx-design-system/framework';

import { useMedicationConfigOverride } from '../../../contexts/DrugProgramProvider';
import type {
    AuthorizedMedicationInfoFragment,
    UnknownMedicationInfoFragment,
} from '../../../graphql/generated/graphql';
import type { PricingInfo } from '../../../util/config/get-pricing';

import MedicationPricing from './MedicationPricing';

type PreviewCashDetailsProps = {
    pricingInfo: PricingInfo;
    medication?:
        | AuthorizedMedicationInfoFragment
        | UnknownMedicationInfoFragment;
};

export const PreviewCashDetails = ({
    pricingInfo,
    medication,
}: PreviewCashDetailsProps) => {
    const { t } = useTranslation();
    const configOverride = useMedicationConfigOverride();

    const ndc = medication
        ? medication.__typename === 'UnknownMedication'
            ? medication.originalNdc || medication.id
            : medication.id
        : '';

    const medicationOverride = configOverride.ndcInfo[ndc];

    // Get the current medication's dose, unit, and regular price
    const currentDose =
        medication?.__typename === 'AuthorizedMedication'
            ? medication.strengthValue || ''
            : medicationOverride?.strengthValue || '';

    return (
        <>
            <MedicationPricing
                pricingInfo={pricingInfo}
                medication={medication}
                data-testid="pharmacy-preview-pricing-card"
            />

            <Stack gap="xs" data-testid="pharmacy-preview-terms-and-conditions">
                {currentDose === '2.5' ? (
                    <Body2>
                        {t('previewDetails.cashDescriptionStartingDose')}
                    </Body2>
                ) : null}
                <Body2>
                    {t('previewDetails.cashDescription1', {
                        reg: constants.unicode.REGISTERED,
                    })}
                </Body2>
                <Body2>{t('previewDetails.cashDescription2')}</Body2>
                <Body2>
                    {t('previewDetails.termsAndConditions.vial.title', {
                        reg: constants.unicode.REGISTERED,
                    })}
                </Body2>
                <Body2>
                    {t('previewDetails.termsAndConditions.vial.body1', {
                        reg: constants.unicode.REGISTERED,
                    })}
                </Body2>
                <Body2>
                    {t('previewDetails.termsAndConditions.vial.body2', {
                        reg: constants.unicode.REGISTERED,
                    })}
                </Body2>
                <Body2>
                    <Trans
                        i18nKey="previewDetails.termsAndConditions.vial.body3"
                        components={{
                            1: <strong />,
                        }}
                        values={{
                            reg: constants.unicode.REGISTERED,
                        }}
                    />
                </Body2>
            </Stack>
        </>
    );
};
